/**
 * After a deploy, a tab that is still open knows only the old file names (e.g. Projects-abc123.js);
 * those files are gone, so opening another screen fails with "error loading dynamically imported
 * module". Reloading once loads the new version. Guarded so a real outage cannot loop.
 */
const KEY = '__rv_chunk_reload';

export function isChunkLoadError(e: unknown): boolean {
  const m = String((e as any)?.message ?? e ?? '');
  return /dynamically imported module|Importing a module script failed|Failed to fetch dynamically|error loading dynamically|Unable to preload CSS|ChunkLoadError|Loading chunk .* failed/i.test(m);
}

/** Reloads the page once per 30 seconds; returns false when it already tried (show an error instead). */
export function reloadForNewVersion(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(KEY) ?? 0);
  } catch {
    /* storage blocked */
  }
  if (Date.now() - last < 30_000) return false;
  try {
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* storage blocked */
  }
  location.reload();
  return true;
}

/** Wraps a lazy import: a missing (old) file reloads the page instead of breaking it. */
export function withChunkReload<T>(load: () => Promise<T>): () => Promise<T> {
  return () =>
    load().catch((e) => {
      if (isChunkLoadError(e) && reloadForNewVersion()) return new Promise<T>(() => undefined); // the page is reloading
      throw e;
    });
}
